import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

interface Holiday {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  description?: string;
  isActive: boolean;
}

interface HolidayDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: HolidayFormData) => Promise<void>;
  editingHoliday: Holiday | null;
}

const holidaySchema = z.object({
  name: z.string().min(1, 'Holiday name is required'),
  startDate: z.string().min(1, 'Start date is required'),
  endDate: z.string().min(1, 'End date is required'),
  description: z.string().optional(),
}).refine((data) => {
  const startDate = new Date(data.startDate);
  const endDate = new Date(data.endDate);
  return endDate >= startDate;
}, {
  message: 'End date must be after or equal to start date',
  path: ['endDate'],
});

type HolidayFormData = z.infer<typeof holidaySchema>;

const HolidayDialog: React.FC<HolidayDialogProps> = ({
  open,
  onClose,
  onSubmit,
  editingHoliday,
}) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<HolidayFormData>({
    resolver: zodResolver(holidaySchema),
    defaultValues: {
      name: '',
      startDate: '',
      endDate: '',
      description: '',
    },
  });

  React.useEffect(() => {
    if (open) {
      if (editingHoliday) {
        reset({
          name: editingHoliday.name,
          startDate: editingHoliday.startDate.split('T')[0], // Convert to YYYY-MM-DD format
          endDate: editingHoliday.endDate.split('T')[0], // Convert to YYYY-MM-DD format
          description: editingHoliday.description || '',
        });
      } else {
        reset({
          name: '',
          startDate: '',
          endDate: '',
          description: '',
        });
      }
    }
  }, [open, editingHoliday, reset]);

  const handleFormSubmit = async (data: HolidayFormData) => {
    try {
      await onSubmit(data);
      onClose();
    } catch (error) {
      // Error handling is done in the parent component
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {editingHoliday ? 'Edit Holiday' : 'Add New Holiday'}
      </DialogTitle>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Holiday Name"
                  fullWidth
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  disabled={isSubmitting}
                />
              )}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Controller
                name="startDate"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Start Date"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.startDate}
                    helperText={errors.startDate?.message}
                    disabled={isSubmitting}
                  />
                )}
              />
              <Controller
                name="endDate"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="End Date"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.endDate}
                    helperText={errors.endDate?.message}
                    disabled={isSubmitting}
                  />
                )}
              />
            </Box>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Description (Optional)"
                  fullWidth
                  multiline
                  rows={3}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                  disabled={isSubmitting}
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained"
            disabled={isSubmitting}
          >
            {isSubmitting 
              ? (editingHoliday ? 'Updating...' : 'Creating...') 
              : (editingHoliday ? 'Update' : 'Create')
            }
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default HolidayDialog;
